import { useMemo, useRef, useState } from "react";
import {
  grantableInvitationRoles,
  isAssetScopedInviteRole,
  isBrandScopedRole,
  isInvitationCreateRole,
  type FacilityDashboardEntry,
  type FacilityDashboardProvider,
  type ObservationPhaseVocabulary,
  type OrgMember,
  type RecipeMeResponse,
  type Role,
} from "@bitacorapro/api-types";
import { brandIdentityOf } from "@bitacorapro/core-engine";
import { EmptyState } from "../components/EmptyState";
import { Icon } from "../components/icons";
import { InviteBrandField } from "../components/InviteBrandField";
import { PageError } from "../components/PageError";
import { PageSkeleton } from "../components/PageSkeleton";
import { QueryBoundary } from "../components/QueryBoundary";
import { Skel } from "../components/Skel";
import { StorePicker } from "../components/StorePicker";
import { ApiError } from "../lib/api/error";
import { useSession } from "../lib/auth/useSession";
import {
  useAssignableAssets,
  useBrandAffiliations,
  useCreateTeamInvitation,
  useFacilityDashboard,
  useMyOrganization,
  useRecipe,
  useResolveScopes,
  useRoster,
  useSetBrandAffiliation,
} from "../lib/api/queries";
import { isFacilityDashboardEnabled } from "../lib/capabilities";
import { isEntryOpen } from "../lib/postventa/entryThread";
import { useQueryView, type QueryView } from "../lib/loading/useQueryView";
import { lastSeenLabel } from "../lib/util";
import { EquipoSkeleton, ProveedoresTableSkeleton, StorePickerSkeleton } from "./EquipoSkeleton";

function providerInitials(p: FacilityDashboardProvider): string {
  return p.shortName
    .replace(/[^A-Za-z]/g, "")
    .slice(0, 2)
    .toUpperCase();
}

function memberInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w.charAt(0).toUpperCase())
      .join("") || "?"
  );
}

// Static fallback labels, facility-flavored — used only while the recipe
// hasn't loaded yet or doesn't supply a label for this role. The picker
// now offers whatever roles a vertical's recipe declares, not just
// facility's, so a non-facility role (e.g. broker_postventa's
// store_manager, labelled "Cliente" in that recipe, not "Jefe de tienda")
// must render under the recipe's own vocabulary — same fix as the PWA's
// cuenta/equipo/invitar/+page.svelte roleMeta().
function roleLabel(role: Role, recipe?: RecipeMeResponse): string {
  const recipeLabel = recipe?.memberRoles.find((r) => r.key === role)?.label;
  if (recipeLabel) return recipeLabel;
  switch (role) {
    case "admin":
      return "Admin";
    case "company_manager":
      return "Gerente";
    case "brand_manager":
      return "Gerente de marca";
    case "store_manager":
      return "Jefe de tienda";
    case "facility_supervisor":
      return "Supervisor";
    case "inspector":
      return "Inspector";
    // Postventa (ADR-0090). Nombres de la minuta del 2026-08-18. El
    // `default` de abajo evita que un rol nuevo rompa la pantalla, pero
    // pinta la clave cruda — que es lo que estos tres casos evitan.
    case "gerente_postventa":
      return "Gerente de postventa";
    case "jefe_proyecto":
      return "Jefe de proyecto";
    case "propietario":
      return "Propietario";
    default:
      return role;
  }
}

// Resolve a membership's scope id to a human name via the resolve-name
// map (built from POST /assets/resolve per ADR-0022). Falls back to the
// shape-only label when names haven't loaded yet or the id failed to
// resolve (foreign-org leakage protection on the server). The
// auth_memberships_scope_exclusive_chk constraint guarantees at most
// one of the three ids is set.
function accessLabel(m: OrgMember, names: Map<string, string>): string {
  if (m.scopeBrandId) return names.get(m.scopeBrandId) ?? "1 marca";
  if (m.scopeAssetGroupId) return names.get(m.scopeAssetGroupId) ?? "1 zona";
  if (m.scopeAssetId) return names.get(m.scopeAssetId) ?? "1 tienda";
  return "Organización";
}

// ADR-0134 — a provider's live jobs are its observations still open in the
// thread (not closed by conformity, not anulada), never the retired
// delegation column.
function activeCount(
  provider: FacilityDashboardProvider,
  entries: FacilityDashboardEntry[],
  vocab: ObservationPhaseVocabulary | undefined
): number {
  return entries.filter((e) => e.providerId === provider.id && isEntryOpen(e, vocab)).length;
}

// A first load that failed, page-level: PageError with Reintentar — the
// same copy QueryBoundary uses, offline included.
function LoadError({
  view,
  label,
}: {
  view: Extract<QueryView<unknown, unknown>, { kind: "error" }>;
  label: string;
}) {
  return view.offline ? (
    <PageError
      title="Sin conexión"
      description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
      onRetry={view.retry}
    />
  ) : (
    <PageError
      title={`No pudimos cargar ${label}`}
      description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
      onRetry={view.retry}
    />
  );
}

// Server error codes of POST /organizations/me/invitations → es-CL copy.
// The two brand codes (ADR-0164) render next to the brand picker when it
// is on screen; every other code renders under the form.
const INVITE_ERROR_MESSAGES: Record<string, string> = {
  ROLE_ESCALATION: "No tienes permiso para invitar con ese rol.",
  SINGLE_BRAND_ORG:
    "Esta organización no tiene más de una marca — no se puede invitar a un Gerente de marca.",
  ROLE_NOT_IN_RECIPE:
    "Ese rol ya no está disponible para invitar en esta organización. Actualiza la página e intenta de nuevo.",
  STORE_MANAGER_REQUIRES_EXACTLY_ONE_STORE:
    "Los jefes de tienda se asignan a exactamente una tienda. Elige una sola tienda e intenta de nuevo.",
  ASSET_OUT_OF_SCOPE:
    "No tienes acceso a una o más de las tiendas seleccionadas. Actualiza la página e intenta de nuevo.",
  NO_ASSIGNABLE_STORES:
    "No quedan tiendas sin asignar dentro de tu alcance. Contacta a un administrador.",
  BRAND_REQUIRED: "Elige la marca que va a gestionar esta persona.",
  BRAND_NOT_IN_ORG: "Esa marca no pertenece a tu organización.",
  BRAND_NOT_ALLOWED: "Este rol no lleva marca.",
  EMAIL_REQUIRED_FOR_ROLE: "Ingresa el correo de la persona.",
  INVITATION_SCOPE_DROPPED:
    "No pudimos guardar la marca de la invitación. Inténtalo de nuevo en unos minutos.",
  // ADR-0165 — a supervisor's brand of affiliation.
  AFFILIATION_REQUIRED: "Elige la marca de este supervisor.",
  AFFILIATION_NOT_IN_ORG: "Esa marca no pertenece a tu organización.",
  AFFILIATION_MISMATCH: "Las tiendas elegidas son de otra marca.",
  AFFILIATION_NOT_ALLOWED: "Este rol no lleva una marca propia.",
  SUPERVISOR_SPANS_BRANDS:
    "Las tiendas elegidas son de más de una marca: un supervisor pertenece a una sola.",
  STORE_BRAND_UNRESOLVED:
    "Una de las tiendas elegidas no tiene marca. Asígnale una antes de invitar.",
};
const BRAND_FIELD_ERROR_CODES = new Set(["BRAND_REQUIRED", "BRAND_NOT_IN_ORG"]);
const AFFILIATION_FIELD_ERROR_CODES = new Set([
  "AFFILIATION_REQUIRED",
  "AFFILIATION_NOT_IN_ORG",
  "AFFILIATION_MISMATCH",
]);

const AFFILIATION_HINT =
  "El supervisor pertenece a una sola marca: es la organización que verá en el panel, y solo se le asignan tiendas de esa marca.";

// The brand of affiliation of one supervisor row (ADR-0165), editable by
// the org's management. Saving rejects a brand its current stores
// contradict; the error shows under the select.
function MemberAffiliationSelect({
  member,
  brands,
  value,
}: {
  member: OrgMember;
  brands: { id: string; name: string }[];
  value: string | undefined;
}) {
  const save = useSetBrandAffiliation();
  const [error, setError] = useState<string | null>(null);
  const errorId = `affiliation-${member.userId}-error`;
  return (
    <div className="field">
      <select
        className="input"
        aria-label={`Marca de ${member.displayName}`}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        value={value ?? ""}
        disabled={save.isPending}
        onChange={(e) => {
          const brandId = e.target.value;
          if (brandId === "") return;
          setError(null);
          save.mutate(
            { userId: member.userId, brandId },
            {
              onError: (err) =>
                setError(
                  (err instanceof ApiError ? INVITE_ERROR_MESSAGES[err.code] : undefined) ??
                    "No pudimos guardar la marca. Inténtalo de nuevo."
                ),
            }
          );
        }}
      >
        <option value="" disabled>
          Sin marca
        </option>
        {brands.map((b) => (
          <option key={b.id} value={b.id}>
            {b.name}
          </option>
        ))}
      </select>
      {error && (
        <span id={errorId} className="auth-error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

// POLICY NOTE (amends ADR-0112, product decision 2026-09-03): three
// independent gates on this screen, none of them the authority — the
// server re-checks every one independently.
//
// READ the roster: TEAM_ROSTER_READ_ROLE_KEYS — admin, company_manager,
// brand_manager, facility_supervisor. `store_manager` is excluded at
// both the Sidebar nav layer and the RequireTeamRosterRead route guard
// in App.tsx — a jefe manages one store, not the org's people, and has
// no invite affordance either, so there's no action behind the read for
// them. GET /auth/memberships itself carries no role gate server-side;
// this is a rendered-surface gate, not an endpoint gate.
//
// INVITE (POST /organizations/me/invitations, ADR-0116 — supersedes the
// ADR-0112-era direct call to auth-svc's POST /auth/invitations):
// platform admins, plus any role passing isInvitationCreateRole (admin,
// company_manager, facility_supervisor at the API layer today), capped
// per INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER. `brand_manager` is
// additionally withheld from the picker unless the org is multibrand
// (brandCount > 1, from GET /organizations/me) — auth-svc enforces the
// same rule server-side (requiresMultibrandOrg, 403 SINGLE_BRAND_ORG on
// the fanned-to POST /auth/invitations); this is the UX half of that
// server gate, not the gate itself. store_manager and facility_supervisor
// invitations now carry a store assignment from this screen too:
// store_manager requires exactly one store; facility_supervisor defaults
// to every currently-unassigned store within the inviter's own scope —
// not the whole org, containment enforced server-side via
// ASSET_OUT_OF_SCOPE. This composes with, and does not relax, the
// existing ADR-0112 escalation cap and ADR-0115 multibrand gate, both
// still enforced by the same unchanged auth-svc endpoint this new
// core-api route fans out to. The platform-admin /admin panel
// (AdminOrgDetail.tsx) keeps its own sibling endpoint,
// POST /admin/orgs/:id/invitations, for inviting into an org the operator
// doesn't belong to.
//
// MANAGE members (deactivate/reactivate, PATCH /auth/memberships/:id):
// platform admins only (PLATFORM_ADMIN_EMAILS allowlist), unchanged —
// same gate as the PWA's /cuenta/equipo screen. When adding a kebab /
// action sheet for this, wrap it in
// `useSession()?.user.platformAdmin === true`.
//
// Every gate on this screen is a UX guard; the server re-checks
// independently and is the authority.
export function Equipo() {
  const recipeQuery = useRecipe();
  const recipe = recipeQuery.data;
  const recipeLoading = recipeQuery.isLoading;
  const facility = useFacilityDashboard(isFacilityDashboardEnabled(recipe));
  // Equipo's own roster render requests the filtered view=roster
  // response (recipe-declared roles only, server-computed `isOwner`
  // per row) — the plain `useMemberships()` unfiltered hook stays
  // reserved for readers like DetalleObservacion.tsx's actor-name
  // resolution that need every member, not just recipe-declared roles.
  const members = useRoster();
  // Memoized (not a bare `?? []` const) — its own downstream useMemos
  // (visibleMemberRows below, scopeRequest further down) depend on
  // referential stability; a fresh `[]` fallback every render would
  // otherwise make both re-derive every render regardless of their own
  // dependency arrays.
  const memberRows: OrgMember[] = useMemo(() => members.data?.members ?? [], [members.data]);

  // Defense-in-depth client-side mirror of the server's view=roster
  // filter — computed once and reused for the count, the empty-check,
  // and the <tbody> map (never three separate reads of the raw array).
  const memberRoleKeys = useMemo(
    () => new Set(recipe?.memberRoles.map((r) => r.key) ?? []),
    [recipe]
  );
  const visibleMemberRows = useMemo(
    () => memberRows.filter((m) => memberRoleKeys.has(m.role)),
    [memberRows, memberRoleKeys]
  );

  // Invite form gate (ADR-0112): platform admins, plus any role passing
  // isInvitationCreateRole (admin, company_manager, facility_supervisor
  // at the API layer today). Everyone else (inspector, store_manager,
  // brand_manager) sees the roster with no invite affordance. Widened
  // below (after grantableRoles resolves) to also require a non-empty
  // grantable-role list — a coarse role-check pass with nothing
  // actually grantable would otherwise render a broken empty affordance.
  const session = useSession();
  const currentRole = session?.organization.role;
  const canInviteRole =
    session?.user.platformAdmin === true ||
    (currentRole !== undefined && isInvitationCreateRole(currentRole));
  const org = useMyOrganization();

  // Recipe-derived grantable-role list (replaces the old static
  // FACILITY_ONBOARDING_ROLE_KEYS/INVITATION_GRANTABLE_ROLE_KEYS_BY_INVITER
  // cap + manual multibrand filter) — a role the active vertical's
  // recipe doesn't declare never appears as an option, and the
  // brand_manager/multibrand gate now lives inside the helper itself.
  // Fails closed to `[]` while the recipe is still loading or failed to
  // load — never falls back to an unfiltered list that the server may
  // then reject.
  // A brand-scoped role (brand_manager, ADR-0164) needs the org's brand
  // list for its picker; an older core-api that doesn't send
  // `brands` can't fill it, so the role is withheld instead of offered
  // with an empty picker.
  const orgBrands = org.data?.brands;
  const grantableRoles: readonly Role[] = useMemo(() => {
    if (recipeLoading || !recipe) return [];
    return grantableInvitationRoles(
      session?.user.platformAdmin === true ? null : (currentRole ?? null),
      recipe,
      {
        platformAdmin: session?.user.platformAdmin === true,
        brandCount: org.data?.brandCount ?? null,
      }
    ).filter((r) => !isBrandScopedRole(r) || orgBrands !== undefined);
  }, [
    recipeLoading,
    recipe,
    session?.user.platformAdmin,
    currentRole,
    org.data?.brandCount,
    orgBrands,
  ]);

  // NOT a "hide the whole invite panel" gate — the panel itself renders
  // whenever `canInviteRole` is true (see the JSX below), and shows an
  // explanatory message ("No puedes invitar ningún rol desde aquí con tu
  // permiso actual.") when grantableRoles is empty, rather than
  // disappearing. `canInvite` only gates the assignable-assets fetch a
  // few lines down — pointless to fetch stores for a role picker that
  // has nothing to pick.
  const canInvite = canInviteRole && grantableRoles.length > 0;

  // Brand of affiliation (ADR-0165): only in an org with more than one
  // brand — with one, the server fills it in. Read off the recipe
  // primitive, never a role key.
  const multiBrand = (orgBrands?.length ?? 0) > 1;
  const callerOrgScoped =
    session?.user.platformAdmin === true ||
    recipe?.memberRoles.find((r) => r.key === currentRole)?.scopeType === "organization";
  const hasAffiliationRoles =
    recipe?.memberRoles.some((r) => r.brandIdentity === "affiliation") === true;
  const canEditAffiliation = callerOrgScoped && multiBrand && hasAffiliationRoles;
  const affiliations = useBrandAffiliations(canEditAffiliation);
  const affiliationByUser = useMemo(
    () => new Map((affiliations.data?.affiliations ?? []).map((a) => [a.userId, a.brandId])),
    [affiliations.data]
  );

  const createInvitation = useCreateTeamInvitation();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<Role>(grantableRoles[0] ?? "store_manager");
  const [inviteFormError, setInviteFormError] = useState<string | null>(null);
  const [lastInviteUrl, setLastInviteUrl] = useState<string | null>(null);
  // brand_manager invitations carry the brand (ADR-0164). Never
  // preselected; cleared on every role change.
  const [inviteBrandId, setInviteBrandId] = useState("");
  const [inviteBrandError, setInviteBrandError] = useState<string | null>(null);
  const brandSelectRef = useRef<HTMLSelectElement | null>(null);

  // The <select> must never submit a role outside the current grant cap,
  // regardless of what the list looked like at mount.
  const effectiveInviteRole = grantableRoles.includes(inviteRole)
    ? inviteRole
    : (grantableRoles[0] ?? inviteRole);

  // Store assignment (ADR-0116). assetScopeSelected gates both the
  // fetch and the picker's visibility.
  const assetScopeSelected = isAssetScopedInviteRole(effectiveInviteRole);
  const brandScopeSelected = isBrandScopedRole(effectiveInviteRole);
  const affiliationSelected =
    recipe !== undefined &&
    brandIdentityOf(recipe, effectiveInviteRole) === "affiliation" &&
    multiBrand;
  const assignableAssets = useAssignableAssets(canInvite && assetScopeSelected);
  const storesView = useQueryView(assignableAssets);
  // The supervisor's brand, picked before its stores: only that brand's
  // stores are offered (ADR-0165 — a supervisor never spans two brands).
  const [inviteAffiliationId, setInviteAffiliationId] = useState("");
  const [inviteAffiliationError, setInviteAffiliationError] = useState<string | null>(null);
  const affiliationSelectRef = useRef<HTMLSelectElement | null>(null);
  const pickerStores = useMemo(() => {
    const all = assignableAssets.data?.assets ?? [];
    if (!affiliationSelected) return all;
    return inviteAffiliationId === "" ? [] : all.filter((a) => a.brandId === inviteAffiliationId);
  }, [assignableAssets.data, affiliationSelected, inviteAffiliationId]);

  // null = no explicit operator edit yet, use the role's computed
  // default. Non-null once the operator has touched the picker.
  const [assetIdsOverride, setAssetIdsOverride] = useState<string[] | null>(null);

  const defaultAssetIds = useMemo(() => {
    if (effectiveInviteRole !== "facility_supervisor") return [];
    // Default = every assignable store NOT already assigned
    // (hasSupervisor === false). This deliberately mirrors what the
    // backend itself computes and enforces server-side when assetIds is
    // omitted — the frontend pre-selection should match what the backend
    // will actually do, so the operator sees an accurate preview, not a
    // guess that gets silently narrowed server-side.
    return pickerStores.filter((a) => !a.hasSupervisor).map((a) => a.id);
  }, [effectiveInviteRole, pickerStores]);

  const selectedAssetIds = assetIdsOverride ?? defaultAssetIds;

  async function handleCreateInvitation(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setInviteFormError(null);
    setLastInviteUrl(null);
    const trimmed = inviteEmail.trim().toLowerCase();
    if (!trimmed.includes("@")) {
      setInviteFormError("Ingresa un email válido.");
      return;
    }
    if (affiliationSelected && inviteAffiliationId === "") {
      setInviteAffiliationError(INVITE_ERROR_MESSAGES.AFFILIATION_REQUIRED ?? null);
      affiliationSelectRef.current?.focus();
      return;
    }
    setInviteAffiliationError(null);
    if (effectiveInviteRole === "facility_supervisor" && selectedAssetIds.length === 0) {
      setInviteFormError("Selecciona al menos una tienda para el supervisor.");
      return;
    }
    if (effectiveInviteRole === "store_manager" && selectedAssetIds.length !== 1) {
      setInviteFormError("Selecciona exactamente una tienda para el jefe de tienda.");
      return;
    }
    if (brandScopeSelected && inviteBrandId === "") {
      setInviteBrandError(INVITE_ERROR_MESSAGES.BRAND_REQUIRED ?? null);
      brandSelectRef.current?.focus();
      return;
    }
    setInviteBrandError(null);
    try {
      const res = await createInvitation.mutateAsync({
        email: trimmed,
        role: effectiveInviteRole,
        ...(assetScopeSelected && selectedAssetIds.length > 0
          ? { assetIds: selectedAssetIds }
          : {}),
        ...(brandScopeSelected ? { scopeBrandId: inviteBrandId } : {}),
        ...(affiliationSelected ? { affiliationBrandId: inviteAffiliationId } : {}),
      });
      setInviteEmail("");
      setAssetIdsOverride(null);
      setInviteBrandId("");
      setInviteAffiliationId("");
      setLastInviteUrl(res.acceptUrl);
    } catch (err) {
      const mapped = err instanceof ApiError ? INVITE_ERROR_MESSAGES[err.code] : undefined;
      if (
        mapped &&
        brandScopeSelected &&
        err instanceof ApiError &&
        BRAND_FIELD_ERROR_CODES.has(err.code)
      ) {
        setInviteBrandError(mapped);
        brandSelectRef.current?.focus();
        return;
      }
      if (
        mapped &&
        affiliationSelected &&
        err instanceof ApiError &&
        AFFILIATION_FIELD_ERROR_CODES.has(err.code)
      ) {
        setInviteAffiliationError(mapped);
        affiliationSelectRef.current?.focus();
        return;
      }
      setInviteFormError(mapped ?? (err instanceof Error ? err.message : String(err)));
    }
  }

  // Collect the unique scope ids across all members so the resolve
  // endpoint sees a deduped request. The hook is disabled when all
  // three lists are empty (every member has organization scope).
  const scopeRequest = useMemo(() => {
    const brandIds = new Set<string>();
    const assetGroupIds = new Set<string>();
    const assetIds = new Set<string>();
    for (const m of memberRows) {
      if (m.scopeBrandId) brandIds.add(m.scopeBrandId);
      if (m.scopeAssetGroupId) assetGroupIds.add(m.scopeAssetGroupId);
      if (m.scopeAssetId) assetIds.add(m.scopeAssetId);
    }
    return {
      brandIds: Array.from(brandIds),
      assetGroupIds: Array.from(assetGroupIds),
      assetIds: Array.from(assetIds),
    };
  }, [memberRows]);

  const scopes = useResolveScopes(scopeRequest);

  const scopeNames = useMemo(() => {
    const map = new Map<string, string>();
    if (!scopes.data) return map;
    for (const b of scopes.data.brands) map.set(b.id, b.name);
    for (const g of scopes.data.assetGroups) map.set(g.id, g.name);
    for (const a of scopes.data.assets) map.set(a.id, a.name);
    return map;
  }, [scopes.data]);

  // The page body waits on the roster (needed by every recipe) AND the
  // recipe: memberRoleKeys/grantableRoles both depend on it, so rendering
  // past the roster alone briefly showed an empty or wrong roster/picker.
  // The «Proveedores externos» panel is facility_management-specific
  // (isFacilityDashboardEnabled) and has its own section boundary —
  // folding it into this gate made the whole page wait forever for a
  // non-facility org, whose disabled useFacilityDashboard query never
  // settles (see lib/capabilities.ts). useQueryView tells loading from
  // error from disabled, so a failed or disabled query never leaves the
  // skeleton on screen; the header frame renders from the first paint.
  const recipeView = useQueryView(recipeQuery);
  const rosterView = useQueryView(members);
  const showProveedores = isFacilityDashboardEnabled(recipe);
  // The «Proveedores externos» subtitle reads the same view as its section
  // body (QueryBoundary below derives the same kind from the same query):
  // a held first-load error or an offline pause shows no subtitle, never a
  // skeleton pulsing next to the section error.
  const facilityView = useQueryView(facility, { isEmpty: (d) => d.byProvider.length === 0 });
  const bodyReady = recipeView.kind !== "loading" && rosterView.kind !== "loading";

  function renderBody() {
    if (recipeView.kind === "error") return <LoadError view={recipeView} label="tu equipo" />;
    if (rosterView.kind === "error") return <LoadError view={rosterView} label="tu equipo" />;
    if (!bodyReady) {
      const visible = [recipeView, rosterView].some((v) => v.kind === "loading" && v.visible);
      const slow = [recipeView, rosterView].some((v) => v.kind === "loading" && v.slow);
      return (
        <>
          <PageSkeleton label="Cargando tu equipo…" visible={visible}>
            <EquipoSkeleton
              showInvite={canInviteRole}
              showProveedores={recipe !== undefined && showProveedores}
            />
          </PageSkeleton>
          {slow && (
            <div className="notice-banner notice-banner--action">
              <span>
                <strong>Esto está tardando más de lo normal.</strong> Revisa tu conexión. Seguimos
                intentando.
              </span>
              <button
                type="button"
                className="btn btn--secondary btn--sm"
                onClick={() => {
                  if (recipeView.kind === "loading") void recipeQuery.refetch();
                  if (rosterView.kind === "loading") void members.refetch();
                }}
              >
                Reintentar
              </button>
            </div>
          )}
        </>
      );
    }
    return renderContent();
  }

  // A refresh that failed over kept data keeps the data on screen.
  const refreshFailed =
    (recipeView.kind === "ready" && recipeView.refreshFailed) ||
    (rosterView.kind === "ready" && rosterView.refreshFailed);

  function renderContent() {
    return (
      <div className="stack-gap">
        {refreshFailed && (
          <div className="notice-banner notice-banner--action">
            <span>No pudimos actualizar. Mostrando lo último que tienes guardado.</span>
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => {
                void recipeQuery.refetch();
                void members.refetch();
              }}
            >
              Reintentar
            </button>
          </div>
        )}
        <div className="panel">
          <div className="panel__head">
            <div className="panel__title">Miembros internos</div>
            <div className="panel__sub mono">
              {`${visibleMemberRows.length} ${visibleMemberRows.length === 1 ? "miembro" : "miembros"}`}
            </div>
          </div>
          <div className="panel__body--flush">
            {visibleMemberRows.length === 0 ? (
              <EmptyState
                title="Sin miembros todavía"
                description="Invita a tu primer compañero de equipo para empezar a trabajar juntos en BitácoraPro."
              />
            ) : (
              <table className="dtable">
                <thead>
                  <tr>
                    <th scope="col">Miembro</th>
                    <th scope="col">Rol</th>
                    <th scope="col">Acceso</th>
                    <th scope="col">Última actividad</th>
                    <th scope="col">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleMemberRows.map((m) => {
                    const disabled = m.disabledAt != null;
                    return (
                      <tr key={m.userId}>
                        <td>
                          <div className="user-cell">
                            <span className="avatar">{memberInitials(m.displayName)}</span>
                            <div className="cell-with-sub">
                              <span className="cell-strong">{m.displayName}</span>
                              <span className="cell-sub">{m.email}</span>
                            </div>
                          </div>
                        </td>
                        <td>{roleLabel(m.role, recipe)}</td>
                        <td>
                          {canEditAffiliation &&
                          recipe !== undefined &&
                          orgBrands !== undefined &&
                          brandIdentityOf(recipe, m.role) === "affiliation" ? (
                            <div className="cell-with-sub">
                              <MemberAffiliationSelect
                                member={m}
                                brands={orgBrands}
                                value={affiliationByUser.get(m.userId)}
                              />
                              <span className="cell-sub">{accessLabel(m, scopeNames)}</span>
                            </div>
                          ) : (
                            accessLabel(m, scopeNames)
                          )}
                        </td>
                        <td className="cell-meta">{lastSeenLabel(m.lastSeenAt)}</td>
                        <td>
                          {disabled ? (
                            <span className="st-pill st-pill--idle">Desactivado</span>
                          ) : (
                            <span className="st-pill st-pill--ok">Activo</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {canInviteRole && (
            <div className="panel__body">
              <form
                onSubmit={(e) => void handleCreateInvitation(e)}
                noValidate
                className="inline-form"
              >
                <h3
                  className="cell-meta"
                  style={{ marginTop: 0, marginBottom: 4, fontWeight: "inherit" }}
                >
                  Invitar miembro
                </h3>
                {grantableRoles.length === 0 ? (
                  <p className="cell-meta">
                    No puedes invitar ningún rol desde aquí con tu permiso actual.
                  </p>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                      alignItems: "flex-start",
                    }}
                  >
                    <input
                      id="invite-email"
                      type="email"
                      className="input"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      placeholder="correo@dominio.cl"
                      maxLength={254}
                      disabled={createInvitation.isPending}
                      required
                      style={{ flex: "1 1 240px" }}
                    />
                    <select
                      className="input"
                      value={effectiveInviteRole}
                      onChange={(e) => {
                        // Every role change re-derives the picker's
                        // selection fresh for the new role (all-unassigned
                        // default for supervisor, empty for jefe) rather
                        // than carrying over a stale array from the
                        // previous role.
                        setAssetIdsOverride(null);
                        setInviteBrandId("");
                        setInviteBrandError(null);
                        setInviteAffiliationId("");
                        setInviteAffiliationError(null);
                        setInviteRole(e.target.value as Role);
                      }}
                      disabled={createInvitation.isPending}
                      aria-label="Rol del invitado"
                      style={{ flex: "0 0 auto", minWidth: 160 }}
                    >
                      {grantableRoles.map((r) => (
                        <option key={r} value={r}>
                          {roleLabel(r, recipe)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="submit"
                      className="btn btn--primary btn--sm"
                      disabled={
                        createInvitation.isPending ||
                        (brandScopeSelected && (orgBrands?.length ?? 0) === 0)
                      }
                    >
                      {createInvitation.isPending ? "Enviando…" : "Invitar"}
                    </button>
                  </div>
                )}
                {grantableRoles.length > 0 && brandScopeSelected && orgBrands && (
                  <InviteBrandField
                    ref={brandSelectRef}
                    brands={orgBrands}
                    value={inviteBrandId}
                    onChange={(v) => {
                      setInviteBrandId(v);
                      if (v !== "") setInviteBrandError(null);
                    }}
                    error={inviteBrandError}
                    idPrefix="equipo-invite"
                  />
                )}
                {grantableRoles.length > 0 && affiliationSelected && orgBrands && (
                  <InviteBrandField
                    ref={affiliationSelectRef}
                    brands={orgBrands}
                    value={inviteAffiliationId}
                    onChange={(v) => {
                      setInviteAffiliationId(v);
                      setAssetIdsOverride(null);
                      if (v !== "") setInviteAffiliationError(null);
                    }}
                    error={inviteAffiliationError}
                    idPrefix="equipo-invite-affiliation"
                    label="Marca del supervisor"
                    hint={AFFILIATION_HINT}
                  />
                )}
                {grantableRoles.length > 0 && assetScopeSelected && (
                  <div style={{ marginTop: 12 }}>
                    {storesView.kind === "loading" ? (
                      <>
                        {/* A field inside the page's form: no second status
                            announcement (the page owns the only one). */}
                        <PageSkeleton
                          label="Cargando tiendas…"
                          kind="section"
                          announce={false}
                          visible={storesView.visible}
                        >
                          <StorePickerSkeleton />
                        </PageSkeleton>
                        {storesView.slow && (
                          <span className="cell-meta">
                            Esto está tardando más de lo normal.{" "}
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => void assignableAssets.refetch()}
                            >
                              Reintentar
                            </button>
                          </span>
                        )}
                      </>
                    ) : storesView.kind === "error" ? (
                      // A field-level load error inside the form: inline,
                      // never moves focus (the operator may be typing).
                      <span className="auth-error" role="alert">
                        {storesView.offline
                          ? "Sin conexión: no pudimos cargar las tiendas."
                          : "No pudimos cargar las tiendas."}{" "}
                        <button
                          type="button"
                          className="btn btn--ghost btn--sm"
                          onClick={storesView.retry}
                        >
                          Reintentar
                        </button>
                      </span>
                    ) : affiliationSelected && inviteAffiliationId === "" ? (
                      <span className="cell-meta">
                        Elige primero la marca del supervisor para ver sus tiendas.
                      </span>
                    ) : pickerStores.length === 0 ? (
                      <div className="store-picker store-picker--empty">
                        <div className="store-picker__legend">
                          {affiliationSelected
                            ? "Esta marca no tiene tiendas asignables dentro de tu alcance."
                            : "No tienes tiendas asignables todavía."}
                        </div>
                      </div>
                    ) : (
                      <>
                        {effectiveInviteRole === "facility_supervisor" && (
                          <p className="cell-meta" style={{ marginBottom: 8 }}>
                            Por defecto, un supervisor cubre todas las tiendas sin asignar dentro de
                            tu alcance. Desmarca las que no correspondan.
                          </p>
                        )}
                        <StorePicker
                          stores={pickerStores}
                          selectedIds={selectedAssetIds}
                          onChange={(next) => setAssetIdsOverride(next)}
                          disabled={createInvitation.isPending}
                          mode={effectiveInviteRole === "store_manager" ? "single" : "multi"}
                          legend={
                            effectiveInviteRole === "store_manager" && selectedAssetIds.length !== 1
                              ? "Elige la tienda de este jefe (obligatorio)"
                              : undefined
                          }
                          className={
                            effectiveInviteRole === "facility_supervisor" &&
                            assetIdsOverride === null &&
                            selectedAssetIds.length === pickerStores.length
                              ? "store-picker--all-selected"
                              : undefined
                          }
                        />
                      </>
                    )}
                  </div>
                )}
                {inviteFormError && (
                  <div className="auth-error" role="alert" style={{ marginTop: 8 }}>
                    {inviteFormError}
                  </div>
                )}
                {lastInviteUrl && (
                  <div className="cell-meta" role="status" style={{ marginTop: 8 }}>
                    {/* The response (acceptUrl + assetResults) says nothing
                        about email dispatch, so the copy follows the recipe:
                        a facility invitation is emailed by auth-svc (the
                        self-service fan-out, ADR-0116) and the link is a
                        backup; on a lifecycle vertical the link is the
                        delivery. Keyed on the dashboards capability, never
                        on recipe.slug (§0.4(6)). */}
                    {isFacilityDashboardEnabled(recipe)
                      ? "Invitación enviada. Link de acceso:"
                      : "Invitación creada. Cópialo y envíalo tú:"}{" "}
                    <code
                      style={{
                        wordBreak: "break-all",
                        display: "inline-block",
                        maxWidth: "100%",
                      }}
                    >
                      {lastInviteUrl}
                    </code>
                  </div>
                )}
              </form>
              <p className="cell-meta" style={{ marginTop: 12 }}>
                Las invitaciones de Jefe de tienda y Supervisor ya asignan tienda desde este
                formulario: un jefe se asigna a exactamente una tienda; un supervisor cubre, por
                defecto, las tiendas sin asignar dentro de tu alcance (no toda la organización). Usa
                el panel de admin para invitar en nombre de otra organización.
              </p>
            </div>
          )}
        </div>

        {showProveedores && (
          <div className="panel">
            <div className="panel__head">
              <div className="panel__title">Proveedores externos</div>
              <div className="panel__sub mono">
                {(facilityView.kind === "ready" || facilityView.kind === "empty") &&
                facility.data ? (
                  `${facility.data.byProvider.length} activos · destinatarios de observaciones`
                ) : facilityView.kind === "loading" ? (
                  <span data-skel-visible={facilityView.visible}>
                    <Skel variant="text" width={220} />
                  </span>
                ) : null}
              </div>
            </div>
            <div className="panel__body--flush">
              <QueryBoundary
                query={facility}
                label="los proveedores"
                kind="section"
                skeleton={<ProveedoresTableSkeleton />}
                isEmpty={(d) => d.byProvider.length === 0}
                empty={
                  <EmptyState
                    title="Sin proveedores"
                    description="No hay proveedores cargados en esta organización todavía."
                  />
                }
              >
                {(d) => (
                  <table className="dtable">
                    <thead>
                      <tr>
                        <th>Proveedor</th>
                        <th>Rubro</th>
                        <th className="col-num">SLA contrato</th>
                        <th className="col-num">Cierre promedio</th>
                        <th>Cumplimiento</th>
                        <th className="col-num">Activas</th>
                      </tr>
                    </thead>
                    <tbody>
                      {d.byProvider.map((p) => {
                        const onTime =
                          p.avgCloseDays !== null
                            ? p.avgCloseDays <= (p.defaultSlaDays ?? 30)
                            : null;
                        const activas = activeCount(p, d.entries, recipe);
                        return (
                          <tr key={p.id}>
                            <td>
                              <div className="user-cell">
                                <span className="avatar avatar--ext">{providerInitials(p)}</span>
                                <div className="cell-with-sub">
                                  <span className="cell-strong">{p.legalName}</span>
                                  <span className="cell-sub">
                                    {p.shortName} · contratista externo
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td>{p.specialty}</td>
                            <td className="col-num">{p.defaultSlaDays} días</td>
                            <td className="col-num">
                              {p.avgCloseDays !== null ? `${p.avgCloseDays.toFixed(1)} días` : "—"}
                            </td>
                            <td>
                              {onTime === null ? (
                                <span className="st-pill st-pill--idle">Sin cierres</span>
                              ) : onTime ? (
                                <span className="st-pill st-pill--ok">Dentro SLA</span>
                              ) : (
                                <span className="st-pill st-pill--crit">
                                  <Icon.TrendDown s={10} /> Sobre SLA
                                </span>
                              )}
                            </td>
                            <td className="col-num">{activas}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </QueryBoundary>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div className="page-header__lead">
          <div className="page-header__eyebrow">
            {bodyReady ? (
              <>
                {visibleMemberRows.length} miembros
                {showProveedores &&
                  facility.data &&
                  ` · ${facility.data.byProvider.length} proveedores`}
              </>
            ) : (
              <Skel variant="text" width={150} />
            )}
          </div>
          <h1 className="page-header__title">Equipo</h1>
          <p className="page-header__sub">
            {recipeView.kind === "loading" ? (
              <Skel variant="text" width="55%" />
            ) : showProveedores ? (
              "Miembros internos de la organización y proveedores externos a quienes delegar observaciones."
            ) : (
              "Miembros internos de la organización."
            )}
          </p>
        </div>
      </div>
      {renderBody()}
    </div>
  );
}
